import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiConfigService } from '../api/api-config.service';
import { PlatformService } from './platform.service';

// TipoPlugin do backend — 10 = Logística, 6 = Marketplace, 5 = ERP (ver
// integracoes-config.component.ts, TIPO_CATEGORY — fonte única do mapeamento completo).
const TIPO_PLUGIN_LOGISTICA = 10;
const TIPO_PLUGIN_MARKETPLACE = 6;
const TIPO_PLUGIN_ERP = 5;
// `TipoPlugin.BI` = 13 (KRAM.SDK `IManifesto.cs`: Kommerce 0 … Logistica 10, Alerta 11, Cilindro 12, BI 13).
const TIPO_PLUGIN_BI = 13;

export interface LogisticOperatorOption {
  id: string;
  name: string;
}

export interface MarketplaceOriginOption {
  id: string;
  name: string;
}

export interface BiIntegrationOption {
  id: string;
  name: string;
  active: boolean;
}

export interface OutboundIntegrationOption {
  id: string;
  name: string;
}

export interface GrupoPropriedade {
  codigo: string;
  label: string | null;
  ordem: number | null;
}

/**
 * Campo elegível dentro de uma Propriedade `tipo: "filter"` — a **whitelist** que
 * o Manifesto declara para o `dx-filter-builder`. O front nunca inventa campo:
 * o que não vier aqui não é filtrável.
 *
 * Contrato KRAM.SDK#1169 (front gowo.hub.front#482), **ainda não publicado** —
 * por isso `campos` é opcional na Propriedade: manifesto sem o campo continua
 * renderizando como hoje.
 */
export interface CampoFiltro {
  nome: string | null;
  label: string | null;
  /** Tipo lógico do campo; mapeado para o `dataType` do FilterBuilder. */
  tipo: string | null;
  /**
   * Lista de valores do campo, quando ele é um enum/domínio fechado (ex.:
   * `Status` de produto — `Ativo=1, Inativo=2, SemEstoque=4, PreLancamento=8,
   * NaoListado=16, Pendente=32`). Presente ⇒ o field vira **lookup** e os
   * operadores ficam restritos a igual/diferente.
   *
   * `KeyValuePair<object,string>[]` do lado do backend, que serializa no mesmo
   * `{ key, value }` já usado por `Propriedade.opcoes` — `key` é o valor
   * gravado no filtro (numérico, no caso de enum) e `value` é o rótulo.
   *
   * **Não usar `HasFlag`/máscara de bits**: apesar de potências de 2, o enum
   * NÃO é `[Flags]` (KRAM.SDK#1169) — só igualdade faz sentido. Se um dia virar
   * `[Flags]`, este filtro passa a mentir em silêncio.
   */
  valores?: { key: boolean | number | string; value: string }[] | null;
  /**
   * Domínio dinâmico do campo (`categorias` | `marcas` — contrato KRAM.SDK#1177):
   * a lista de opções vem da API do tenant, não do Manifesto. O Manifesto declara
   * só o NOME do domínio; a rota correspondente é resolvida no front
   * (`ProductDomainLookupService`), único lugar que conhece endpoints.
   *
   * Mutuamente exclusivo com `valores` — se ambos vierem, `valores` (estático)
   * tem precedência.
   */
  dominio?: string | null;
  /**
   * Nota de contexto exibida abaixo do builder, quando o campo tem uma pegadinha
   * que o rótulo sozinho não conta — ex.: o default de `Status` é `Pendente`,
   * então `Situação = Ativo` exclui produto nunca classificado.
   *
   * Nome **confirmado**: `CampoFiltravel.Descricao` no SDK (KRAM.SDK#1169 /
   * PR SDK#1171), padronizado com `Propriedade.Descricao`. O front havia
   * assumido `hint` enquanto o contrato não existia, e por isso nenhuma nota
   * renderizava (gowo.hub.front#484).
   *
   * O front NÃO hardcoda a nota por campo: o texto é sempre do Manifesto.
   */
  descricao?: string | null;
}

export interface Propriedade {
  tipo: string | null;
  nome: string | null;
  label: string | null;
  padrao: string | null;
  opcoes: { key: boolean | number | string; value: string }[] | null;
  obrigatorio: boolean;
  somenteLeitura: boolean;
  grupo: string | null;
  /** Só em `tipo: "filter"` — ver `CampoFiltro`. Ausente nos demais tipos. */
  campos?: CampoFiltro[] | null;
  /**
   * Nota de contexto do campo, renderizada como `helpText` do item do `dx-form`
   * (`shared/utils/manifest-form-items.ts`). Nome confirmado: `Propriedade.Descricao` no SDK
   * (KRAM.SDK#1169 / PR SDK#1171), padronizado com `CampoFiltro.descricao`. Ausente até a
   * #818 — por isso a nota nunca renderizava (gowo.hub.front#484/#818).
   */
  descricao?: string | null;
  /**
   * Só em `tipo: "lista"` (KRAM.SDK#1704, aditivo): presente ⇒ a UI carrega as opções do host
   * em vez de `opcoes`. Domínio conhecido hoje: `vendedores` (`ManifestListDomainService`).
   */
  dominio?: string | null;
}

/** `ModoInstalacaoOAuth` do SDK (`KRAM.Plugin/IManifesto.cs`) — valores do enum C#, sem conversor de string registrado (mesma convenção numérica de `tipo`/`tipoConexao` nesta interface). */
export const INSTALACAO_OAUTH_HUB = 0;
export const INSTALACAO_OAUTH_EXTERNO = 1;
export const INSTALACAO_OAUTH_AMBOS = 2;

export interface IManifesto {
  id: string;
  nome: string | null;
  descricao: string | null;
  logo: string | null;
  url: string | null;
  tipo: number;
  tipoConexao: number;
  criptoSonda: boolean;
  possuiWebhook: boolean;
  clientesSuportados: string[] | null;
  propriedades: Propriedade[] | null;
  grupos: GrupoPropriedade[] | null;
  /**
   * Onde a autorização OAuth desta plataforma começa — `0` Hub, `1` Externo, `2` Ambos.
   * **Ausente/`null` É o contrato** (não bug de serialização): `DefaultIgnoreCondition =
   * WhenWritingNull` no backend omite a chave quando o plugin não declara o membro —
   * plugin que não declara não é OAuth e não entra no catálogo de "Nova integração" (D9,
   * issue #816). Não reconstruir essa exclusão como lista fixa no Angular (Shopee, Amazon).
   */
  instalacaoOAuth?: 0 | 1 | 2 | null;
  /**
   * Visibilidade do plugin para o tenant — `enum Publico` do SDK (`KRAM.Plugin/IManifesto.cs:106-110`):
   * `0` **Cliente** (default — os ~80 plugins existentes, nunca tocados), `1` **Interno**
   * (infraestrutura — Grafana, MultiChat — só usuário GOWO `Acesso-Admin` vê de origem).
   * **Ausente/`undefined` equivale a Cliente** (valor padrão do SDK), nunca a Interno. Existe no
   * contrato desde a SDK 10.2.10.9 (em produção), confirmado no OpenAPI vigente de `hmg`
   * (`IManifesto.publico`). A regra real é do servidor (kram.hub.api#2595 fase 2:
   * `CompanyServices.cs:85` não devolve a linha virtual Interno a quem não é `Acesso-Admin`,
   * `:172` recusa criar) — o filtro em `NovaIntegracaoComponent.filteredIntegracoes` é só defesa
   * em profundidade (gowo.hub.front#834).
   */
  publico?: 0 | 1;
}

/**
 * Resposta de `POST /api/company/integration/{id}/test-connection` (kram.hub.api#3180).
 * Sempre `200` nos três casos; `unavailable` não é sucesso nem falha (o plugin não sabe testar).
 */
export interface TestarConexaoResponseDTO {
  status: 'success' | 'failure' | 'unavailable';
  message: string | null;
}

export interface IntegrationDTO {
  id: string;
  idPlugin: string;
  name: string | null;
  plugin: IManifesto;
  type: number;
  active: boolean;
  configuration: string | null;
  /**
   * Nomes dos campos `tipo = "password"` do manifesto que têm valor gravado (kram.hub.api#2983).
   * O `GET`/`POST`/`PUT` devolvem esses campos como `""` em `configuration`: o front nunca vê o
   * segredo, só sabe que existe. `""` no `PUT` e na perna 1 do `validate` significa "manter".
   */
  segredosDefinidos?: string[];
  description: string | null;
  /**
   * Status do plugin na administração do Hub (`active`/`inactive`), não o `active` da LINHA do
   * tenant acima — são conceitos diferentes (gowo.hub.front#833). Vem de `GET
   * /api/company/integration` desde kram.hub.api#2600 (fase 1 da #2595, em `hmg`/`main`):
   * junção por `IdPlugin` = `BillingIntegrationPrice.PlatformId`; plugin nunca ofertado
   * comercialmente conta como `inactive`. Controla se a plataforma aparece desabilitada para o
   * tenant em "Nova integração" (D2) — nunca confundir com `active` (toggle da integração já
   * conectada).
   */
  adminStatus: 'active' | 'inactive';
}

/**
 * Resposta de `POST /api/company/integration/validate` (`ValidarIntegracaoResponseDTO`,
 * `CompanyController.cs:417-430`, confirmado em `hmg` — A4, issue #812). `pendingId` e
 * `segredoClaim` só vêm preenchidos quando `sucesso: true` **e** o pendente foi de fato
 * persistido no backend — hoje nunca para o Mercado Livre (o `validate` dele responde 500
 * antes de chegar lá, L1 da spec de OAuth, medida em produção em 03/10/2026).
 */
export interface ValidarIntegracaoResponseDTO {
  sucesso: boolean;
  key: string | null;
  urlRedirect: string | null;
  dados: string | null;
  token: string | null;
  pendingId?: string;
  segredoClaim?: string;
}

/** Estados do `IntegrationPending` (spec OAuth §5.2, decisão D2 — nomenclatura em português). */
export type PendingStatus = 'AguardandoAutorizacao' | 'Autorizada' | 'Vinculada' | 'Falhou' | 'Expirada';

/**
 * Resposta de `GET /api/company/integration/pending/{id}` (A1, kram.hub.api#2536, mergeada —
 * confirmado contra `CompanyController.cs` em `hmg`, issue #814). `conta` é nulável: o
 * backend devolve `null` quando o `Key` do plugin é um GUID opaco (caso do Bling pós-S4, que
 * não expõe identificador de conta) — nunca mostrar esse GUID como se fosse rótulo. Nunca
 * carrega `Dados`, `Token` nem o `segredoClaim`.
 */
export interface PendingStatusDTO {
  id: string;
  plataforma: string | null;
  conta: string | null;
  status: PendingStatus;
  expiraEm: string | null;
  erro: string | null;
}

/**
 * Resposta de sucesso de `POST /api/company/integration/pending/{id}/claim` (A2,
 * kram.hub.api#2537, mergeada). `substituiuContaExistente` é `true` quando o índice único
 * `(CompanyId, IdPlugin)` já tinha uma integração dessa plataforma na empresa — o claim
 * substitui, não duplica, e avisa que substituiu (D7).
 */
export interface ClaimIntegrationPendingResponseDTO {
  integrationId: string;
  substituiuContaExistente: boolean;
}

@Injectable({ providedIn: 'root' })
export class CompanyIntegrationService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(ApiConfigService);
  private readonly platformService = inject(PlatformService);

  getAll(): Observable<IntegrationDTO[]> {
    return this.http.get<IntegrationDTO[]>(this.api.url('/api/company/integration'));
  }

  /**
   * Operadores logísticos (TipoPlugin 10) **ativos** do tenant — única fonte
   * para o filtro "Operador Logístico" (issue #722). Antes desta issue, cada
   * tela filtrava só por `plugin.tipo`, ignorando `IntegrationDTO.active` —
   * um operador desligado continuava aparecendo e podia ser selecionado.
   */
  getActiveLogisticOperators(): Observable<LogisticOperatorOption[]> {
    return this.getAll().pipe(
      map(integrations => {
        const operators = new Map<string, LogisticOperatorOption>();
        for (const it of integrations) {
          if (!it.active || it.plugin?.tipo !== TIPO_PLUGIN_LOGISTICA || !it.plugin.id) continue;
          operators.set(it.plugin.id, {
            id: it.plugin.id,
            name: it.plugin.nome ?? this.platformService.getName(it.plugin.id),
          });
        }
        return [...operators.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      }),
    );
  }

  /**
   * Vínculos de marketplace (TipoPlugin 6) **ativos** do tenant — fonte do filtro "Origem" do
   * Painel de Separação (issue #748/DSEP-F1, spec §4.2/§4.7: "Origens não são hardcoded: saem
   * do fan-out de vínculos do pedido por tipo de plugin"). Mesmo padrão de
   * `getActiveLogisticOperators()` (issue #722), trocando só o `TipoPlugin` filtrado. Não inclui
   * "ERP-born" — pedido sem vínculo de marketplace — que é um rótulo do domínio, não uma
   * integração; a tela que consome esta lista acrescenta essa opção à parte.
   */
  getActiveMarketplaceOrigins(): Observable<MarketplaceOriginOption[]> {
    return this.getAll().pipe(
      map(integrations => {
        const origins = new Map<string, MarketplaceOriginOption>();
        for (const it of integrations) {
          if (!it.active || it.plugin?.tipo !== TIPO_PLUGIN_MARKETPLACE || !it.plugin.id) continue;
          origins.set(it.plugin.id, {
            id: it.plugin.id,
            name: it.plugin.nome ?? this.platformService.getName(it.plugin.id),
          });
        }
        return [...origins.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      }),
    );
  }

  /**
   * Integrações **ativas** com vínculo de SAÍDA — fonte do filtro "Plataforma" de Notas Fiscais
   * (issue #887). "Vínculo de saída" aqui reproduz EXATAMENTE `InvoiceSituacaoIntegracao.EhSaida`
   * do backend (`kram.hub.api#2895`, confirmado lendo `InvoiceListFilter.cs`/PR #2900):
   * `p => p.PluginType != TipoPlugin.ERP` — **qualquer** tipo de plugin ativo, exceto o ERP (a
   * origem da nota, nunca um destino). Decisão desta implementação: usar essa whitelist-por-
   * exclusão (todo tipo exceto ERP), e não combinar só `getActiveLogisticOperators()` +
   * `getActiveMarketplaceOrigins()` (Logística + Marketplace) — o predicado real do backend não
   * se limita a esses dois tipos (ex.: Frete, tipo 2, também é um vínculo de saída válido e
   * entra em `situacaoIntegracao`), e um filtro mais estreito que o contrato real deixaria
   * `?plataforma=<id>` escolher integrações que a opção do select nem oferece.
   */
  getActiveOutboundIntegrations(): Observable<OutboundIntegrationOption[]> {
    return this.getAll().pipe(
      map(integrations => {
        const outbound = new Map<string, OutboundIntegrationOption>();
        for (const it of integrations) {
          if (!it.active || it.plugin?.tipo === TIPO_PLUGIN_ERP || !it.plugin?.id) continue;
          outbound.set(it.plugin.id, {
            id: it.plugin.id,
            name: it.plugin.nome ?? this.platformService.getName(it.plugin.id),
          });
        }
        return [...outbound.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      }),
    );
  }

  /**
   * Integrações de BI (TipoPlugin 13) do tenant, **ativas ou não** — fonte do filtro "Integração"
   * da aba Execuções de BI & Analytics (#948). Inclui as inativas de propósito: o histórico delas
   * continua na grade (módulo pausado) e o filtro não pode ficar vazio; `active` deixa a tela
   * marcá-las.
   */
  getBiIntegrations(): Observable<BiIntegrationOption[]> {
    return this.getAll().pipe(
      map(integrations => {
        const bi = new Map<string, BiIntegrationOption>();
        for (const it of integrations) {
          if (it.plugin?.tipo !== TIPO_PLUGIN_BI || !it.plugin.id) continue;
          // Duas integrações do mesmo plugin: basta uma ativa para a opção contar como ativa.
          const ativa = it.active || (bi.get(it.plugin.id)?.active ?? false);
          bi.set(it.plugin.id, {
            id: it.plugin.id,
            name: it.plugin.nome ?? this.platformService.getName(it.plugin.id),
            active: ativa,
          });
        }
        return [...bi.values()].sort((x, y) => x.name.localeCompare(y.name, 'pt-BR'));
      }),
    );
  }

  getById(id: string): Observable<IntegrationDTO> {
    return this.http.get<IntegrationDTO>(this.api.url(`/api/company/integration/${id}`));
  }

  /**
   * Testa a integração GRAVADA (a api lê a `Configuration` do banco). Não grava nada.
   * **Nunca adicionar retry aqui:** a rota tem rate limit (429) e a tela bloqueia o botão em voo.
   */
  testarConexao(id: string): Observable<TestarConexaoResponseDTO> {
    return this.http.post<TestarConexaoResponseDTO>(this.api.url(`/api/company/integration/${id}/test-connection`), null);
  }

  update(id: string, dto: Partial<IntegrationDTO>): Observable<IntegrationDTO> {
    return this.http.put<IntegrationDTO>(this.api.url(`/api/company/integration/${id}`), dto);
  }

  /**
   * Primeira conexão de uma plataforma é `POST`, nunca `PUT` — a `Integration` ainda não
   * existe (linha virtual do catálogo, `id` vazio, §2.4 da spec de OAuth). Mesmo endpoint do
   * `update`, mas em POST: o backend resolve "adicionar ou atualizar" pelo `idPlugin` do
   * corpo, nunca pela URL (gowo.hub.front#740).
   */
  create(dto: Partial<IntegrationDTO>): Observable<IntegrationDTO> {
    return this.http.post<IntegrationDTO>(this.api.url('/api/company/integration'), dto);
  }

  /** Caso estreito de `create()`: instalar pelo card do catálogo, só com `idPlugin` e nome. */
  install(idPlugin: string, name: string): Observable<IntegrationDTO> {
    return this.create({ idPlugin, name });
  }

  toggleActive(id: string, active: boolean): Observable<IntegrationDTO> {
    return this.http.put<IntegrationDTO>(
      this.api.url(`/api/company/integration/${id}/active`),
      { active }
    );
  }

  /**
   * Valida os parâmetros vindos da plataforma (callback OAuth, Fluxo 2) ou do formulário do
   * Fluxo 1 — `POST /api/company/integration/validate`, anônimo, já publicado (confirmado em
   * `hmg`). **Sempre responde 200** quando o plugin roda, inclusive com `sucesso: false`; o
   * único erro HTTP é `404` para plataforma desconhecida. Olhar o corpo, não só o status.
   *
   * **Nunca adicionar retry aqui.** O `validate` do Mercado Livre responde **500 para
   * qualquer `code`** — defeito permanente do plugin (L1 da spec de OAuth, medido em
   * homologação em 03/10/2026, kram.hub.api#2544/S3), não falha transitória de rede. Repetir a
   * chamada produziria três 500 em vez de um, sem nenhuma chance de sucesso.
   */
  validate(plataform: string, parameters: Record<string, string>): Observable<ValidarIntegracaoResponseDTO> {
    return this.http.post<ValidarIntegracaoResponseDTO>(
      this.api.url('/api/company/integration/validate'),
      { plataform, parameters },
    );
  }

  /**
   * Estado do pendente — `GET /api/company/integration/pending/{id}`.
   *
   * `segredoClaim` vai no header `X-Claim-Secret`, **nunca** em query string — decisão do lead
   * (kram.hub.api, PR #2561; revisão da issue #812): query string cai em log de acesso do
   * servidor, no `Referer` e no histórico do navegador, exatamente a fraqueza que o segundo
   * fator existe para fechar (§10.5 da spec de OAuth).
   *
   * Pré-claim, a API autoriza pelo `segredoClaim` (não há tenant ainda — o pendente nasce
   * órfão); a consulta **verifica e nunca consome** o segredo, então chamar isto repetidas
   * vezes no polling (`IntegracaoOAuthService.iniciarPolling`) é seguro. Pós-claim
   * (`status: 'Vinculada'`), passa a exigir o tenant de quem perguntou, não mais o segredo.
   */
  pendingStatus(id: string, segredoClaim: string): Observable<PendingStatusDTO> {
    return this.http.get<PendingStatusDTO>(
      this.api.url(`/api/company/integration/pending/${id}`),
      { headers: { 'X-Claim-Secret': segredoClaim } },
    );
  }

  /**
   * Reivindica o pendente para a **empresa ativa** — `POST /api/company/integration/pending/{id}/claim`.
   *
   * Corrigido contra o contrato real (A2, kram.hub.api#2537, mergeada — issue #814):
   * **não leva `companyId` nem `nome` no corpo.** A empresa é a do `X-Domain-App` (o
   * `tenantInterceptor` já anexa a partir do tenant ativo — por isso quem chama precisa
   * trocar o tenant ativo, via `TenantService.setTenant`, ANTES desta chamada); backend
   * exige administrador **desse** tenant. `Integration.Name` não existe neste endpoint —
   * quem usa (F4) grava o nome depois, com `update(integrationId, { name })`.
   *
   * Erros vêm com `{ erro: string }` pronto para exibir — nunca remontar a partir do status
   * HTTP (403 cobre segredo inválido e falta de permissão; 409 cobre expirado, já vinculado,
   * ainda não autorizado e conflito de gravação; cada um com a frase certa no corpo).
   */
  claim(id: string, segredoClaim: string): Observable<ClaimIntegrationPendingResponseDTO> {
    return this.http.post<ClaimIntegrationPendingResponseDTO>(
      this.api.url(`/api/company/integration/pending/${id}/claim`),
      { segredoClaim },
    );
  }
}
